// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, PropsWithChildren, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

interface OwnProps {
    title?: string;
    expandByDefault?: boolean;
    type?: "widget" | "component";
}

export interface ToggleFiltersStyles {
    container?: InjectableCss;
    toggleButton?: ClickablePresentationProps;
    zoneWrapper?: InjectableCss;
    text?: TypographyProps;
}

export const toggleFiltersStyles: ToggleFiltersStyles = {};

const styles = toggleFiltersStyles;
const ToggleFilters: FC<PropsWithChildren<OwnProps>> = ({
    title = translate("Filter Results"),
    expandByDefault = false,
    children,
    type = "component",
}) => {
    const [isOpen, setIsOpen] = useState(expandByDefault);
    const transitionRef = useRef<HTMLDivElement>(null);

    return (
        <StyledWrapper data-test-selector="toggle-filters-widget" {...styles.container}>
            <Clickable
                aria-label="toggle"
                {...styles.toggleButton}
                aria-expanded={isOpen}
                onClick={() => {
                    setIsOpen(prevState => !prevState);
                }}
            >
                <Typography {...styles.text}>{title}</Typography>
                <Icon src={isOpen ? ChevronUp : ChevronDown} size={15} color="primary" className="toggle-icon" />
            </Clickable>
            <StyledWrapper {...styles.zoneWrapper} className={isOpen ? "expanded" : "collapsed"}>
                <CSSTransition nodeRef={transitionRef} in={isOpen} timeout={300} classNames="filter-zone" unmountOnExit>
                    <div ref={transitionRef}>
                        {type === "component" && (
                            <StyledWrapper className="toggle-children-component">{children}</StyledWrapper>
                        )}
                        {type === "widget" && children}
                    </div>
                </CSSTransition>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ToggleFilters;
